import { humanizeEnum } from './format';

const UUID_RE = /\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*/gi;

/** Older system-written descriptions ended with a raw record id ("Expense claim 3f2a-…"); hide the id. */
export const readableDescription = (description?: string | null): string =>
  (description ?? '').replace(UUID_RE, ' ').replace(/\s+/g, ' ').trim();

const REFERENCE_LABELS: Record<string, string> = {
  SALARY_PAYMENT: 'Salary payment',
  FX_REVALUATION: 'Unrealised FX revaluation',
  FX_REVALUATION_AUTO_REVERSAL: 'Unrealised FX revaluation (auto-reversal)',
  FX_REVALUATION_UNDO: 'Unrealised FX revaluation (undo)',
  FX_REVALUATION_AUTO_REVERSAL_UNDO: 'Unrealised FX revaluation (undo of auto-reversal)',
  SALES_INVOICE: 'Sales invoice',
  SALES_INVOICE_REVERSAL: 'Sales invoice reversal',
  RECEIPT: 'Customer receipt',
  RECEIPT_REVERSAL: 'Customer receipt reversal',
  EXPENSE_CLAIM: 'Expense claim',
  EXPENSE_PAYMENT: 'Expense payment',
  EXPENSE_ADVANCE: 'Cash advance',
  FIXED_ASSET_ACQUISITION: 'Fixed asset purchase',
  REGISTER_EXISTING: 'Fixed asset opening balance',
  DEPRECIATION_ACCRUAL: 'Depreciation',
  ASSET_DISPOSAL: 'Fixed asset disposal',
  GOODS_RECEIPT: 'Goods receipt',
  BANK_ADJUSTMENT: 'Bank adjustment',
  JOURNAL_REVERSAL: 'Reversal of another entry',
  EXP_REALIZATION: 'Export realization',
  EXP_REALIZATION_REVERSAL: 'Export realization reversal',
  EXP_SHIPMENT: 'Export shipment (receivable)',
  EXP_SHIPMENT_REVERSAL: 'Export shipment reversal',
  EXP_WRITE_OFF: 'Export receivable write-off',
  EXP_WRITE_OFF_REVERSAL: 'Export receivable write-off reversal',
  CASH_INCENTIVE_CLAIM: 'Cash incentive claim',
  CASH_INCENTIVE_CLAIM_REVERSAL: 'Cash incentive claim reversal',
  CASH_INCENTIVE_RECEIPT: 'Cash incentive received',
  CASH_INCENTIVE_RECEIPT_REVERSAL: 'Cash incentive receipt reversal',
  WPPF_PROVISION: 'WPPF provision',
  CONTRA: 'Contra voucher',
  GROUP_BALANCE_RECLASS: 'Group account reclassification',
  RECURRING_JOURNAL: 'Recurring journal',
};

export const referenceLabel = (referenceType?: string | null): string =>
  referenceType ? (REFERENCE_LABELS[referenceType] ?? humanizeEnum(referenceType)) : '';

/** The label mid-sentence: "sales invoice", but acronyms keep their case ("FA segment transfer", "WPPF provision"). */
export const referenceLabelInline = (referenceType?: string | null): string => {
  const label = referenceLabel(referenceType);
  const firstWord = label.split(' ')[0];
  return firstWord.length > 1 && firstWord === firstWord.toUpperCase() ? label : label.charAt(0).toLowerCase() + label.slice(1);
};

// Entries written by these documents can be reversed from the ledger screen (no other way exists).
const LEDGER_REVERSIBLE = new Set(['TRANSACTION', 'FIXED_ASSET_ACQUISITION', 'REGISTER_EXISTING',
  'DEPRECIATION_ACCRUAL', 'ASSET_DISPOSAL', 'GROUP_BALANCE_RECLASS', 'CONTRA', 'RECURRING_JOURNAL']);

/** True when another document wrote this entry and must be reversed from that document. */
export const mustReverseFromSource = (referenceType?: string | null): boolean =>
  !!referenceType && !LEDGER_REVERSIBLE.has(referenceType);

/** True when voiding leaves the source document untouched (warn before doing it). */
export const hasSourceDocument = (referenceType?: string | null): boolean => !!referenceType && referenceType !== 'CONTRA';
